import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { useMutation } from "@tanstack/react-query";
import { toastError } from "../../shared/services/errors/erros-handler";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Grid,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import InputSetores from "../../shared/components/formulario/inputSetores/inputSetor";
import InputPlanoDeContas from "../../shared/components/formulario/inputPlanoDeContas/inputPlanoDeContas";
import { MovementTypeEnum } from "../financeiro/schema/gerar-movimentacao-obra.schema";
import { SelectNfsEmpresa } from "../../shared/components/formulario/input-select-nfs/selectNfs";
import { useEffect, useState } from "react";
import InputData from "../../shared/components/formulario/input-data/inputData";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import {
  MovementOriginEnum,
  PeriodicityEnum,
} from "../../shared/utils/enum/enum";
import { SelectObra } from "../../shared/components/formulario/input-select-obra/selectObra";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { useSectorById } from "../../shared/components/formulario/inputSetores/use-sector";
import { ContasAReceberFormData, contasAReceberSchema } from "./schema/criar-contas-a-receber.schema";
import { contasAReceberService } from "./service/criar-contas-a-receber.service";

export function CriarContasReceber() {
  const methods = useForm<ContasAReceberFormData>({
    resolver: yupResolver(contasAReceberSchema),
    defaultValues: {},
  });

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
    watch,
  } = methods;

  const navigate = useNavigate();

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: ContasAReceberFormData) => {
      const result = await contasAReceberService.criar(data);
      return result;
    },
    onSuccess: (result) => {
      toast.success("Conta a Receber criada com sucesso!");
      navigate("/financeiro/contas-a-receber");
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  const [isRelatedToNfs, setIsRelatedToNfs] = useState(false);
  const sector = watch("idSector");
  const idCliente = watch("clientId");
  const idObra = watch("workId");
  const setorSelecionado = useSectorById(sector);

  return (
    <BaseLayout title="Adicionar Receita">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit((formData) => mutate(formData))}
        aoClicarBotaoVoltar={() => navigate("/financeiro/contas-a-pagar")}
      />

      <Box
        component="form"
        onSubmit={handleSubmit((formData) => mutate(formData))}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px )"
      >
        <Grid container display="flex" spacing={2}>
          <Grid item xs={12} md={4}>
            <InputSetores
              name="idSector"
              label="Setor de Origem"
              methods={methods}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputPlanoDeContas
              name="chartOfAccountsId"
              label="Plano de Contas"
              tiPosMovimentacao={MovementTypeEnum.INCOME || ""}
              methods={methods}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descrição"
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputMascarado
              name="totalValue"
              methods={methods}
              label="Valor Total"
              mask={mascaraMoedaReal}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputData
              name="purchaseDate"
              methods={methods}
              label="Data de Vencimento"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="numberOfInstallments"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Quantidade de Parcelas"
                  type="number"
                  error={!!errors.numberOfInstallments}
                  helperText={errors.numberOfInstallments?.message}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="periodicity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Periodicidade"
                  error={!!errors.periodicity}
                  helperText={errors.periodicity?.message}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">Selecione</MenuItem>
                  <MenuItem value={PeriodicityEnum.MONTHLY}>Mensal</MenuItem>
                  <MenuItem value={PeriodicityEnum.BIWEEKLY}>
                    Bimestral
                  </MenuItem>
                  <MenuItem value={PeriodicityEnum.WEEKLY}>Semestral</MenuItem>
                </TextField>
              )}
            />
          </Grid>
        </Grid>
        {setorSelecionado?.origin === MovementOriginEnum.WORK && (
          <Box display="flex" mt={3} gap={2}>
            <Controller
              name="clientId"
              control={control}
              render={({ field }) => (
                <SelectCliente
                  value={field.value || ""}
                  onChange={(id, name) => {
                    field.onChange(id);
                  }}
                  required={
                    setorSelecionado?.origin === MovementOriginEnum.WORK
                  }
                />
              )}
            />
            <Controller
              name="workId"
              control={control}
              render={({ field }) => (
                <SelectObra
                  value={field.value || ""}
                  onChange={(id, name) => {
                    field.onChange(id);
                  }}
                  idCliente={idCliente || ""}
                  required={
                    setorSelecionado?.origin === MovementOriginEnum.WORK
                  }
                />
              )}
            />
          </Box>
        )}

        <Box mt={3}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              Esta movimentação está relacionada a uma entrada de nota fiscal?
            </FormLabel>
            <RadioGroup
              row
              value={isRelatedToNfs ? "sim" : "nao"}
              onChange={(e) => setIsRelatedToNfs(e.target.value === "sim")}
            >
              <FormControlLabel value="sim" control={<Radio />} label="Sim" />
              <FormControlLabel value="nao" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>
          {isRelatedToNfs && (
            <Controller
              name="idNfs"
              control={control}
              render={({ field }) => (
                <SelectNfsEmpresa
                  name="idNfs"
                  methods={methods}
                  idWork={idObra || ""}
                  label="Selecione uma Nota Fiscal"
                  required={true}
                />
              )}
            />
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={4}>
          <button type="submit" style={{ display: "none" }} />
        </Box>
      </Box>
    </BaseLayout>
  );
}
